// src/components/salesinvoice/SalesInvoiceNew.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { decryptData } from '../../utils/encrypt';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import {
  DISPATCH_DETAILS_BY_IDS_API,
  SAVE_INVOICE,
  GET_LOADING_POINTS_ENDPOINT,
  INVOICE_SUPPORTING_DOCUMENT_ENDPOINT,
  INVOICE_WABA_ENDPOINT,
  CHECK_TALLY_INVOICE_UNIQUE_API, // ⬅️ import
} from '../../config/api';

function SalesInvoiceNew() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const dispatchIdsParam = queryParams.get('dispatch_ids') || '';
  const dispatchIds = useMemo(
    () => (dispatchIdsParam ? dispatchIdsParam.split(',') : []),
    [dispatchIdsParam]
  );

  const [dispatchData, setDispatchData] = useState([]);
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // Header
  const [tallyInvoiceNo, setTallyInvoiceNo] = useState('');
  const [tallyInvoiceDate, setTallyInvoiceDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  // Uniqueness state
  const [checkingUnique, setCheckingUnique] = useState(false);
  const [invoiceNoDuplicate, setInvoiceNoDuplicate] = useState(null); // null | true | false
  const [invoiceNoMessage, setInvoiceNoMessage] = useState('');

  // Loading points
  const [loadingPoints, setLoadingPoints] = useState([]);
  const [selectedLoadingPoint, setSelectedLoadingPoint] = useState('');
  const [loadingPointQty, setLoadingPointQty] = useState('');
  const [addedLoadingPoints, setAddedLoadingPoints] = useState([]);

  // Documents (optional)
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({}); // { [document_id]: File }

  const [errorMsg, setErrorMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false); // lock UI after success

  // ---------- INIT ----------
  useEffect(() => {
    const storedUser = localStorage.getItem('suyog_user');
    const storedOrg = localStorage.getItem('suyog_selected_org');

    if (storedUser) {
      const parsed = decryptData(storedUser);
      setUserId(parsed?.user_id || null);
    }

    if (storedOrg) {
      const parsedOrg = JSON.parse(storedOrg);
      setOrgId(parsedOrg?.value || null);
    }
  }, []);

  useEffect(() => {
    if (!userId || !orgId || dispatchIds.length === 0) return;

    let isMounted = true;
    (async () => {
      try {
        const res = await fetch(DISPATCH_DETAILS_BY_IDS_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, org_id: orgId, dispatch_ids: dispatchIds }),
        });
        const json = await res.json();
        if (!isMounted) return;
        if (json.success) {
          setDispatchData(json.data || []);
          setErrorMsg(null);
        } else {
          setErrorMsg(json.error || 'No dispatch data found.');
        }
      } catch (err) {
        if (!isMounted) return;
        setErrorMsg('Server error. Please try again later.');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [userId, orgId, dispatchIdsParam]);

  useEffect(() => {
    if (!orgId) return;

    (async () => {
      try {
        const res = await fetch(GET_LOADING_POINTS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_id: orgId }),
        });
        const json = await res.json();
        if (json.success) setLoadingPoints(json.records || []);
      } catch { /* no-op */ }
    })();
  }, [orgId]);

  const customerId = dispatchData[0]?.customer_id;
  useEffect(() => {
    if (!customerId) return;

    let isMounted = true;
    (async () => {
      try {
        const res = await fetch(INVOICE_SUPPORTING_DOCUMENT_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_id: customerId }),
        });
        const json = await res.json();
        if (isMounted && json.success) {
          setSupportingDocs(json.documents || []);
        }
      } catch { /* no-op */ }
    })();

    return () => {
      isMounted = false;
    };
  }, [customerId]);

  // ---------- HELPERS ----------
  const parseQty = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalQty = useMemo(() => {
    return addedLoadingPoints.reduce((sum, lp) => {
      const q = parseQty(lp.qty);
      return sum + (q > 0 ? q : 0);
    }, 0);
  }, [addedLoadingPoints]);

  const material = dispatchData[0];
  const rate = Number(material?.price || 0);
  const gstPct = Number(material?.gst || 0);
  const subtotal = useMemo(() => totalQty * rate, [totalQty, rate]);
  const gstAmt = useMemo(() => subtotal * (gstPct / 100), [subtotal, gstPct]);
  const grandTotal = useMemo(() => subtotal + gstAmt, [subtotal, gstAmt]);

  const headerInvalid = useMemo(() => {
    return !tallyInvoiceNo.trim() || !tallyInvoiceDate;
  }, [tallyInvoiceNo, tallyInvoiceDate]);

  const loadingPointsInvalid = useMemo(() => {
    if (addedLoadingPoints.length === 0) return true;
    return addedLoadingPoints.some(lp => parseQty(lp.qty) <= 0);
  }, [addedLoadingPoints]);

  const canSave = useMemo(() => {
    if (totalQty <= 0) return false;
    if (headerInvalid) return false;
    if (loadingPointsInvalid) return false;
    if (!dispatchData.length) return false;
    if (checkingUnique) return false;
    if (invoiceNoDuplicate === true) return false;
    return true;
  }, [
    totalQty, headerInvalid, loadingPointsInvalid, dispatchData.length,
    checkingUnique, invoiceNoDuplicate
  ]);

  const disableReasons = useMemo(() => {
    const reasons = [];
    if (!dispatchData.length) reasons.push('Dispatch data not loaded');
    if (headerInvalid) reasons.push('Enter invoice number & date');
    if (addedLoadingPoints.length === 0) reasons.push('Add at least one Loading Point');
    if (totalQty <= 0) reasons.push('Total Qty must be greater than 0');
    if (loadingPointsInvalid) reasons.push('All Loading Point quantities must be > 0');
    if (checkingUnique) reasons.push('Checking invoice number…');
    if (invoiceNoDuplicate === true) reasons.push('Invoice number already exists');
    return reasons;
  }, [
    dispatchData.length, headerInvalid, addedLoadingPoints.length, totalQty,
    loadingPointsInvalid, checkingUnique, invoiceNoDuplicate
  ]);

  const handleAddLoadingPoint = () => {
    if (!selectedLoadingPoint || !loadingPointQty) return;
    const selected = loadingPoints.find(p => p.name === selectedLoadingPoint);
    if (!selected) return;

    const exists = addedLoadingPoints.find(lp => lp.id === selected.id);
    if (exists) {
      alert('Loading Point already added.');
      return;
    }

    const qty = parseQty(loadingPointQty);
    setAddedLoadingPoints(prev => [...prev, { id: selected.id, name: selected.name, qty }]);
    setSelectedLoadingPoint('');
    setLoadingPointQty('');
  };

  const handleChangeLpQty = (idx, value) => {
    setAddedLoadingPoints(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], qty: value };
      return next;
    });
  };

  const handleRemoveLp = (idx) => {
    setAddedLoadingPoints(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDocumentUpload = (documentId, file) => {
    setUploadedDocs(prev => ({ ...prev, [String(documentId)]: file }));
  };

  // ---------- Invoice No uniqueness check (on blur) ----------
  const checkInvoiceNoUnique = async () => {
    const inv = tallyInvoiceNo.trim();
    if (!inv || !orgId) {
      setInvoiceNoDuplicate(null);
      setInvoiceNoMessage('');
      return;
    }
    try {
      setCheckingUnique(true);
      setInvoiceNoMessage('Checking…');

      const res = await fetch(CHECK_TALLY_INVOICE_UNIQUE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId, tally_invoice_no: inv }),
      });
      const json = await res.json();

      const exists =
        (typeof json.exists === 'boolean' && json.exists) ||
        (json.data && typeof json.data.exists === 'boolean' && json.data.exists) ||
        false;

      if (exists) {
        setInvoiceNoDuplicate(true);
        setInvoiceNoMessage('Invoice number already exists. Please enter a unique number.');
      } else {
        setInvoiceNoDuplicate(false);
        setInvoiceNoMessage('Looks good. This invoice number is available.');
      }
    } catch {
      setInvoiceNoDuplicate(true);
      setInvoiceNoMessage('Could not verify uniqueness right now. Please try again.');
    } finally {
      setCheckingUnique(false);
    }
  };

  const preparePayload = () => {
    const items = dispatchData.map(item => ({
      dispatch_id: item.dispatch_id,
      dispatch_material: item.material_name,
      dispatch_qty: totalQty,
      dispatch_unit: item.unit,
      dispatch_price: item.price,
      dispatch_gst: item.gst,
    }));

    return {
      user_id: userId,
      org_id: orgId,
      tally_invoice_no: tallyInvoiceNo.trim(),
      tally_invoice_date: tallyInvoiceDate,
      loading_points: addedLoadingPoints,
      items,
      uploaded_docs: Object.entries(uploadedDocs).map(([docId, file]) => ({
        document_id: docId,
        filename: file?.name || '',
        size_kb: file?.size ? (file.size / 1024).toFixed(2) : '',
      }))
    };
  };

  const triggerWabaForInvoiceItem = async (invoiceItemId) => {
    try {
      await fetch(INVOICE_WABA_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_item_id: invoiceItemId }),
      });
    } catch { /* no-op */ }
  };

  const closeTabOrGoBack = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const saveInvoice = async () => {
    if (!canSave || saving || saved) return;
    setSaving(true);
    try {
      if (invoiceNoDuplicate !== false) {
        setSaving(false);
        alert(invoiceNoMessage || 'Please verify invoice number uniqueness.');
        return;
      }

      const finalPayload = preparePayload();

      const res = await fetch(SAVE_INVOICE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalPayload),
      });

      const json = await res.json();
      if (json.success) {
        const map = json.invoice_item_map || {};
        const jobs = Object.values(map).map((invoiceItemId) => triggerWabaForInvoiceItem(invoiceItemId));
        Promise.allSettled(jobs).finally(() => {});

        alert('✅ Invoice saved successfully.');
        setSaved(true);
        setSaving(false);
        setTimeout(closeTabOrGoBack, 200);
      } else {
        alert('❌ Error: ' + (json.error || 'Unknown error'));
        setSaving(false);
      }
    } catch {
      alert('⚠️ Save failed.');
      setSaving(false);
    }
  };

  const telLink = (num) => (num ? <a href={`tel:${num}`}>{num}</a> : '-');
  const mailLink = (em) => (em ? <a href={`mailto:${em}`}>{em}</a> : '-');

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4 className="text-primary mb-3">Sales Invoice</h4>

        <div className="row g-3">
          {/* ===== Left: Invoice & Loading Points ===== */}
          <div className="col-lg-8">
            <div className="card shadow-sm mb-3">
              <div className="card-header bg-light">
                <strong>Invoice Header</strong>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Tally Invoice No</label>
                    <input
                      type="text"
                      className={`form-control ${invoiceNoDuplicate ? 'is-invalid' : (invoiceNoDuplicate === false ? 'is-valid' : '')}`}
                      value={tallyInvoiceNo}
                      onChange={(e) => {
                        setTallyInvoiceNo(e.target.value);
                        setInvoiceNoDuplicate(null);
                        setInvoiceNoMessage('');
                      }}
                      onBlur={checkInvoiceNoUnique}
                      placeholder="e.g., 24-25/00123"
                      disabled={saved}
                    />
                    {invoiceNoMessage && (
                      <div className={`form-text ${invoiceNoDuplicate ? 'text-danger' : 'text-success'}`}>
                        {checkingUnique ? 'Checking…' : invoiceNoMessage}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Tally Invoice Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={tallyInvoiceDate}
                      onChange={(e) => setTallyInvoiceDate(e.target.value)}
                      disabled={saved}
                    />
                  </div>

                  {/* Loading Point Entry */}
                  <div className="col-md-5">
                    <label className="form-label">Loading Point</label>
                    <select
                      className="form-select"
                      value={selectedLoadingPoint}
                      onChange={(e) => setSelectedLoadingPoint(e.target.value)}
                      disabled={saved}
                    >
                      <option value="">-- Select Loading Point --</option>
                      {loadingPoints.map((p) => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Qty</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className="form-control"
                      value={loadingPointQty}
                      onChange={(e) => setLoadingPointQty(e.target.value)}
                      placeholder="0.000"
                      disabled={saved}
                    />
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <button className="btn btn-primary w-100" onClick={handleAddLoadingPoint} disabled={saved}>
                      ➕ Add
                    </button>
                  </div>
                </div>

                {/* Loading Point Table */}
                {addedLoadingPoints.length > 0 && (
                  <div className="table-responsive mt-3">
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Loading Point</th>
                          <th style={{ minWidth: 120 }}>Qty</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addedLoadingPoints.map((lp, idx) => {
                          const invalid = parseQty(lp.qty) <= 0;
                          return (
                            <tr key={idx} className={invalid ? 'table-warning' : ''}>
                              <td>{idx + 1}</td>
                              <td>{lp.name}</td>
                              <td>
                                <input
                                  type="number"
                                  step="0.001"
                                  min="0"
                                  className="form-control form-control-sm"
                                  value={lp.qty}
                                  onChange={(e) => handleChangeLpQty(idx, e.target.value)}
                                  disabled={saved}
                                />
                                {invalid && (
                                  <small className="text-danger">Qty must be &gt; 0</small>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleRemoveLp(idx)}
                                  title="Remove"
                                  disabled={saved}
                                >
                                  ❌
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="table-light">
                          <td colSpan={2} className="text-end"><strong>Total Qty</strong></td>
                          <td colSpan={2}><strong>{totalQty.toFixed(3)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Supporting Documents (Optional) */}
                {supportingDocs.length > 0 && (
                  <div className="mt-4">
                    <h6 className="text-secondary">Supporting Documents (PDF Only) — Optional</h6>
                    {supportingDocs.map((doc, idx) => (
                      <div className="mb-3" key={idx}>
                        <label className="form-label">{doc.document_name}</label>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="form-control"
                          onChange={(e) => handleDocumentUpload(doc.document_id, e.target.files[0])}
                          disabled={saved}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Dispatch Overview */}
            <div className="card shadow-sm">
              <div className="card-header bg-light">
                <strong>Dispatch Overview</strong>
              </div>
              <div className="card-body">
                {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

                {dispatchData.length > 0 && !errorMsg && (
                  dispatchData.map((item, index) => {
                    // Safe fallbacks for field names
                    const transporterName = item.transporter_name || item.transporters_name || '-';
                    const subTransporterName = item.sub_transporter_name || '-';

                    return (
                      <div key={index} className="mb-4">
                        <div className="row g-2 mb-2 border rounded p-2">
                          <div className="col-md-6">
                            <p><strong>Customer:</strong> {item.account_name}</p>
                            <p><strong>Address:</strong> {item.address}, {item.city}, {item.pincode}</p>
                            <p><strong>Destination:</strong> {item.destination}</p>
                            <p><strong>Delivery Date:</strong> {item.delivery_date}</p>
                            <p><strong>Priority:</strong> {item.priority}</p>
                          </div>

                          {/* Transport Agency block (new details) */}
                          <div className="col-md-6">
                            <p className="mb-1"><strong>Transporter:</strong> {transporterName}</p>
                            <div className="ms-3 small">
                              <div><span className="text-muted">Contact:</span> {item.transporter_contact_person || '-'}</div>
                              <div><span className="text-muted">Mobile:</span> {telLink(item.transporter_mobile)}</div>
                              <div><span className="text-muted">Email:</span> {mailLink(item.transporter_email)}</div>
                            </div>

                            <p className="mt-2 mb-1"><strong>Sub-Transporter:</strong> {subTransporterName}</p>
                            <div className="ms-3 small">
                              <div><span className="text-muted">Contact:</span> {item.sub_transporter_contact_person || '-'}</div>
                              <div><span className="text-muted">Mobile:</span> {telLink(item.sub_transporter_mobile)}</div>
                              <div><span className="text-muted">Email:</span> {mailLink(item.sub_transporter_email)}</div>
                            </div>

                            <hr className="my-2" />
                            <div className="row">
                              <div className="col-sm-6">
                                <div><strong>Vehicle No:</strong> {item.vehicle_no || '-'}</div>
                                <div><strong>Driver:</strong> {item.driver_name || '-'}</div>
                                <div><small className="text-muted">Driver Mobile:</small> {telLink(item.driver_mobile)}</div>
                              </div>
                              <div className="col-sm-6">
                                <div><small className="text-muted">Diesel (Ltrs):</small> {item.diesel_ltr ?? '-'}</div>
                                <div><small className="text-muted">Cash Provided:</small> {item.cash_amount ?? '-'}</div>
                                <div><small className="text-muted">Remark:</small> {item.dispatch_planning_remark || item.remark || '-'}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-bordered table-sm">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Material</th>
                                <th>Qty</th>
                                <th>Unit</th>
                                <th>Rate</th>
                                <th>GST%</th>
                                <th>Total (incl. GST)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>1</td>
                                <td>{item.material_name}</td>
                                <td>{totalQty.toFixed(3)}</td>
                                <td>{item.unit}</td>
                                <td>{item.price}</td>
                                <td>{item.gst}</td>
                                <td>{(totalQty * item.price * (1 + item.gst / 100)).toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* ===== Right: Summary & Save ===== */}
          <div className="col-lg-4">
            <div className="card shadow-sm position-sticky" style={{ top: 16 }}>
              <div className="card-header bg-light">
                <strong>Summary</strong>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Qty</span>
                  <strong>{totalQty.toFixed(3)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Rate</span>
                  <strong>{rate.toFixed(2)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal</span>
                  <strong>{subtotal.toFixed(2)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>GST ({gstPct}%)</span>
                  <strong>{gstAmt.toFixed(2)}</strong>
                </div>
                <hr />
                <div className="d-flex justify-content-between mb-3">
                  <span>Grand Total</span>
                  <strong>₹ {grandTotal.toFixed(2)}</strong>
                </div>

                {!canSave && !saved && (
                  <div className="mb-3">
                    {disableReasons.map((r, i) => (
                      <div key={i} className="badge bg-secondary me-1 mb-1">{r}</div>
                    ))}
                  </div>
                )}

                <button
                  className="btn btn-success w-100"
                  onClick={saveInvoice}
                  disabled={!canSave || saving || saved}
                  title={
                    saved
                      ? 'Invoice saved'
                      : (!canSave ? 'Fix the issues shown above to enable Save' : 'Save Invoice')
                  }
                >
                  {saved ? '✅ Saved' : (saving ? 'Saving…' : '💾 Save Invoice')}
                </button>

                <div className="text-muted small mt-2">
                  {checkingUnique
                    ? 'Checking invoice number…'
                    : 'Save is disabled when total qty is 0 or required header/qty fields are missing.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default SalesInvoiceNew;
